import type {
  MotionEditableField,
  MotionFontInfo,
  MotionFontsInspection,
  MotionGoogleFontInfo,
  MotionLayerFontInfo,
  MotionReplaceFontConfig,
} from "@blings/blings-player/editor-tools";
import { hexToRgba, rgbaArrToHexStr } from "../../../helpers/colors";
import { inferFontOverrideSource } from "../../../helpers/fontOverrides";
import type {
  EditableProperty,
  DocumentMediaAsset,
  DocumentMediaKind,
  FieldLookup,
  FieldConnectionRef,
  FontSelectOption,
  LayerKind,
  VisualEditableField,
  VisualPropertyTarget,
} from "./types";

export const BLEND_MODE_OPTIONS = [
  { value: 0, label: "Normal" },
  { value: 1, label: "Multiply" },
  { value: 2, label: "Screen" },
  { value: 3, label: "Overlay" },
  { value: 4, label: "Darken" },
  { value: 5, label: "Lighten" },
  { value: 10, label: "Difference" },
  { value: 11, label: "Exclusion" },
  { value: 12, label: "Hue" },
  { value: 13, label: "Saturation" },
  { value: 14, label: "Color" },
  { value: 15, label: "Luminosity" },
];

export const CURATED_PROPERTIES = [
  "shapePath",
  "fillColor",
  "textFillColor",
  "solidColor",
  "fillOpacity",
  "text",
  "fontFamily",
  "fontSize",
  "lineHeight",
  "tracking",
  "textJustification",
  "textBoxSize",
  "textBoxPosition",
  "textDirection",
  "verticalAlign",
  "strokeColor",
  "textStrokeColor",
  "strokeOpacity",
  "strokeWidth",
  "textStrokeWidth",
  "rectangleSize",
  "ellipseSize",
  "rectangleRoundness",
  "position",
  "rotation",
  "scale",
  "skew",
  "skewAxis",
  "anchor",
  "opacity",
  "hidden",
  "blendMode",
  "blur",
  "trimStart",
  "trimEnd",
  "trimOffset",
];

const PENDING_FIELD_PREFIX = "__pending__.";

const MEDIA_LAYER_KINDS: Partial<Record<number, DocumentMediaKind>> = {
  2: "image",
  6: "audio",
  7: "video",
  8: "image",
  9: "video",
};
const MEDIA_KIND_ORDER: Record<DocumentMediaKind, number> = {
  image: 0,
  video: 1,
  audio: 2,
};

type AnimationSummary = {
  width: number;
  height: number;
  frameRate: number;
  startFrame: number;
  endFrame: number;
  durationFrames: number;
};

type LayerLike = {
  ty?: number;
  nm?: string;
  uid?: number;
  ind?: number;
  refId?: string;
};

type AssetLike = {
  id?: string;
  nm?: string;
  p?: string;
  u?: string;
  layers?: LayerLike[];
};

type AnimationLike = {
  w?: number;
  h?: number;
  fr?: number;
  ip?: number;
  op?: number;
  layers?: LayerLike[];
  assets?: AssetLike[];
};

type FallbackFieldConfig = {
  property: EditableProperty;
  path: string;
  label: string;
  group: string;
  kind: MotionEditableField["kind"];
  valueType: MotionEditableField["valueType"];
  value: MotionEditableField["value"];
};

const FALLBACK_FIELD_CONFIGS: Record<string, FallbackFieldConfig> = {
  fillColor: {
    property: "fillColor",
    path: "fillColor",
    label: "Fill color",
    group: "Shapes",
    kind: "shape",
    valueType: "color",
    value: [1, 1, 1, 1],
  },
  strokeColor: {
    property: "strokeColor",
    path: "strokeColor",
    label: "Stroke color",
    group: "Shapes",
    kind: "shape",
    valueType: "color",
    value: [0, 0, 0, 1],
  },
  solidColor: {
    property: "solidColor",
    path: "sc",
    label: "Solid color",
    group: "Solid",
    kind: "media",
    valueType: "string",
    value: "#ffffff",
  },
  textFillColor: {
    property: "textFillColor",
    path: "t.d.k.0.s.fc",
    label: "Fill color",
    group: "Text document",
    kind: "text",
    valueType: "color",
    value: [1, 1, 1],
  },
  textStrokeColor: {
    property: "textStrokeColor",
    path: "t.d.k.0.s.sc",
    label: "Stroke color",
    group: "Text document",
    kind: "text",
    valueType: "color",
    value: [0, 0, 0],
  },
};

export const formatLayerKind = (value: string) =>
  value.replace(/^./, (letter) => letter.toUpperCase());

export const getDocumentAnimationSummary = (
  animation: unknown
): AnimationSummary | undefined => {
  const animationRecord = animation as AnimationLike | undefined;
  if (!animationRecord) {
    return undefined;
  }

  const startFrame = toNumber(animationRecord.ip);
  const endFrame = toNumber(animationRecord.op);

  return {
    width: toNumber(animationRecord.w),
    height: toNumber(animationRecord.h),
    frameRate: toNumber(animationRecord.fr),
    startFrame,
    endFrame,
    durationFrames: Math.max(0, endFrame - startFrame),
  };
};

const getLayerDisplayName = (layer: LayerLike, layerIndex: number) =>
  layer.nm || `Layer ${layer.ind ?? layerIndex + 1}`;

const getAssetDisplayName = (asset: AssetLike | undefined, layer: LayerLike) =>
  asset?.nm || layer.nm || asset?.id || "Media asset";

const getAssetUrl = (asset: AssetLike | undefined) =>
  asset?.p ? `${asset.u ?? ""}${asset.p}` : "";

export const getDocumentMediaAssets = (
  animation: unknown
): DocumentMediaAsset[] => {
  const animationRecord = animation as AnimationLike | undefined;
  const assets = animationRecord?.assets ?? [];
  const assetsById = new Map(
    assets
      .filter((asset): asset is AssetLike & { id: string } => !!asset.id)
      .map((asset) => [asset.id, asset])
  );
  const mediaAssetsByKey = new Map<string, DocumentMediaAsset>();
  const visitedPrecompAssetIds = new Set<string>();

  const appendMediaLayer = (
    layer: LayerLike,
    layerIndex: number,
    layerPath: string
  ) => {
    const kind = MEDIA_LAYER_KINDS[layer.ty ?? -1];
    if (!kind || !layer.refId) {
      return;
    }

    const asset = assetsById.get(layer.refId);
    if (!asset) {
      return;
    }

    const key = `${kind}:${layer.refId}`;
    const existingAsset = mediaAssetsByKey.get(key);
    if (existingAsset) {
      existingAsset.occurrenceCount += 1;
      return;
    }

    mediaAssetsByKey.set(key, {
      id: key,
      kind,
      layerName: getLayerDisplayName(layer, layerIndex),
      layerUid: layer.uid,
      layerPath,
      assetId: layer.refId,
      assetName: getAssetDisplayName(asset, layer),
      currentUrl: getAssetUrl(asset),
      occurrenceCount: 1,
    });
  };

  const visitLayers = (layers: LayerLike[] | undefined, path: string) => {
    layers?.forEach((layer, layerIndex) => {
      const layerPath = `${path}[${layerIndex}]`;
      appendMediaLayer(layer, layerIndex, layerPath);

      if (layer.ty !== 0 || !layer.refId) {
        return;
      }

      if (visitedPrecompAssetIds.has(layer.refId)) {
        return;
      }

      const assetIndex = assets.findIndex((asset) => asset.id === layer.refId);
      const asset = assetIndex >= 0 ? assets[assetIndex] : undefined;
      if (!asset?.layers?.length) {
        return;
      }

      visitedPrecompAssetIds.add(layer.refId);
      visitLayers(asset.layers, `assets[${assetIndex}].layers`);
    });
  };

  visitLayers(animationRecord?.layers, "layers");

  return Array.from(mediaAssetsByKey.values()).sort((left, right) => {
    if (left.kind !== right.kind) {
      return MEDIA_KIND_ORDER[left.kind] - MEDIA_KIND_ORDER[right.kind];
    }

    return left.assetName.localeCompare(right.assetName);
  });
};

export const isPendingField = (field: VisualEditableField) =>
  field.rootPath.startsWith(PENDING_FIELD_PREFIX);

export const getPendingFieldPath = (field: VisualEditableField) =>
  isPendingField(field) ? field.path.replace(PENDING_FIELD_PREFIX, "") : null;

export const createFallbackField = (
  target: VisualPropertyTarget | undefined,
  property: EditableProperty,
  options?: { textDocumentIndex?: number; value?: unknown }
): VisualEditableField | undefined => {
  const config = FALLBACK_FIELD_CONFIGS[property];
  if (!target || !config?.path || !target.layerName) {
    return undefined;
  }

  const path =
    typeof options?.textDocumentIndex === "number" &&
    config.path.startsWith("t.d.k.0.s.")
      ? config.path.replace(
          "t.d.k.0.s.",
          `t.d.k.${options.textDocumentIndex}.s.`
        )
      : config.path;

  return {
    ...config,
    path: `${PENDING_FIELD_PREFIX}${path}`,
    rootPath: `${PENDING_FIELD_PREFIX}${path}`,
    value: Object.prototype.hasOwnProperty.call(options ?? {}, "value")
      ? options?.value
      : config.value,
    canAnimate: false,
    isAnimated: false,
    keyframeCount: 0,
    layerName: target.layerName,
    layerUid: target.layerUid,
    assetId: target.assetId,
  };
};

export const getFallbackFillColorProperty = (
  layerKind: LayerKind | undefined,
  canAddFill?: boolean
) => {
  if (layerKind === "text") return "textFillColor";
  if (layerKind === "solid") return "solidColor";
  if (layerKind === "shape" && canAddFill) return "fillColor";
  return undefined;
};

export const getFallbackOutlineColorProperty = (
  layerKind: LayerKind | undefined,
  canAddOutline?: boolean
) => {
  if (layerKind === "text") return "textStrokeColor";
  if (layerKind === "shape" && canAddOutline) return "strokeColor";
  return undefined;
};

export const isNumericValue = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const isNumericVector = (value: unknown): value is number[] =>
  Array.isArray(value) &&
  value.every((entry) => typeof entry === "number" && Number.isFinite(entry));

export const toColorHex = (value: unknown) => {
  if (typeof value === "string" && /^#?[0-9a-f]{6}$/i.test(value)) {
    return value.startsWith("#") ? value : `#${value}`;
  }
  if (!Array.isArray(value) || value.length < 3) {
    return "#ffffff";
  }
  return rgbaArrToHexStr(value.slice(0, 4) as [number, number, number, number]);
};

export const toColorFieldValue = (hex: string, currentValue: unknown) => {
  if (typeof currentValue === "string") {
    return hex;
  }
  const rgba = hexToRgba(hex);
  return Array.isArray(currentValue) && currentValue.length === 3
    ? rgba.slice(0, 3)
    : rgba;
};

export const formatHexLabel = (value: unknown) =>
  toColorHex(value).replace("#", "").toUpperCase();

export const roundToTwoDecimals = (value: number) =>
  Math.round(value * 100) / 100;

export const coerceInputNumber = (value: number | string | null) =>
  typeof value === "number" && Number.isFinite(value)
    ? roundToTwoDecimals(value)
    : undefined;

export const toNumber = (value: unknown) => (isNumericValue(value) ? value : 0);

export const toVector = (field: VisualEditableField | undefined) =>
  field && isNumericVector(field.value) ? field.value : [];

export const buildFieldTarget = (
  field: VisualEditableField
): VisualPropertyTarget => ({
  assetId: field.assetId,
  layerName: field.layerName,
  layerUid: field.layerUid,
});

const TEXT_DOCUMENT_FIELD_KEYS: Record<string, string> = {
  text: "t",
  fontFamily: "f",
  fontSize: "s",
  lineHeight: "lh",
  letterSpacing: "ls",
  tracking: "tr",
  textFillColor: "fc",
  textStrokeColor: "sc",
  textStrokeWidth: "sw",
  textJustification: "j",
  textBoxSize: "sz",
  textBoxPosition: "ps",
  textDirection: "d",
  verticalAlign: "va",
};

type TextDocumentKeyframe = {
  t?: number;
  s?: Record<string, unknown>;
};

type TextDocumentLayer = {
  t?: {
    d?: {
      k?: TextDocumentKeyframe[];
    };
  };
};

export const getActiveTextDocumentIndexForFrame = (
  textDocuments: TextDocumentKeyframe[],
  frame: number
) => {
  let activeIndex = 0;

  textDocuments.forEach((document, index) => {
    if (
      (index === 0 && typeof document.t !== "number") ||
      (typeof document.t === "number" && document.t <= frame)
    ) {
      activeIndex = index;
    }
  });

  return activeIndex;
};

const getTextDocumentFieldPath = (index: number, key: string) =>
  `t.d.k.${index}.s.${key}`;

export const getTextDocumentMutationProperty = (
  field: VisualEditableField
): string => {
  const textDocumentKey = TEXT_DOCUMENT_FIELD_KEYS[field.property];

  return textDocumentKey &&
    new RegExp(`^t\\.d\\.k\\.\\d+\\.s\\.${textDocumentKey}$`).test(
      field.rootPath
    )
    ? field.rootPath
    : field.property;
};

export const resolveTextDocumentFieldsForFrame = (
  fields: VisualEditableField[],
  layer: unknown,
  frame: number
): VisualEditableField[] => {
  const textDocuments = (layer as TextDocumentLayer | undefined)?.t?.d?.k;
  if (!Array.isArray(textDocuments) || !textDocuments.length) {
    return fields;
  }

  const activeIndex = getActiveTextDocumentIndexForFrame(textDocuments, frame);
  const activeDocument = textDocuments[activeIndex]?.s;
  if (!activeDocument || typeof activeDocument !== "object") {
    return fields;
  }

  return fields.map((field) => {
    const textDocumentKey = TEXT_DOCUMENT_FIELD_KEYS[field.property];
    if (!textDocumentKey) {
      return field;
    }

    const path = getTextDocumentFieldPath(activeIndex, textDocumentKey);
    return {
      ...field,
      path,
      rootPath: path,
      value:
        textDocumentKey in activeDocument
          ? activeDocument[textDocumentKey]
          : field.value,
    };
  });
};

const buildFontOptionLabel = (
  fontName: string,
  font?: MotionFontInfo["font"],
  metadata?: MotionFontInfo["metadata"]
) => {
  const family = font?.fFamily || metadata?.family;
  const style = font?.fStyle || metadata?.style;
  const readableName = [family, style].filter(Boolean).join(" ");

  if (!readableName || readableName === fontName) {
    return fontName;
  }

  return `${readableName} (${fontName})`;
};

// Turn a raw font token into a readable form: "_WNBA" -> "WNBA",
// "CondensedBold" -> "Condensed Bold".
const humanizeFontToken = (value?: string | null): string =>
  (value ?? "")
    .replace(/[-_]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

// A PostScript font name encodes the variant after the family, e.g.
// "WNBA-CondensedBold" -> "Condensed Bold". Returns "" when it has no
// distinguishable subfamily part (e.g. "ArialMT").
const getFontVariantFromPostScriptName = (
  fontName?: string | null,
  family?: string | null
): string => {
  if (!fontName) {
    return "";
  }

  const dashIndex = fontName.indexOf("-");
  let subfamily = dashIndex >= 0 ? fontName.slice(dashIndex + 1) : "";

  if (!subfamily && family) {
    // No dash separator: try stripping the family prefix, e.g.
    // "HelveticaNeueBold" with family "Helvetica Neue" -> "Bold".
    const compactFamily = family.replace(/[\s_-]+/g, "").toLowerCase();
    const compactName = fontName.replace(/[\s_-]+/g, "");
    if (compactFamily && compactName.toLowerCase().startsWith(compactFamily)) {
      subfamily = compactName.slice(compactFamily.length);
    }
  }

  return humanizeFontToken(subfamily);
};

// The player derives display names from family + weight only, so distinct
// variants that share a weight (e.g. WNBA Bold / Condensed Bold / Extended
// Bold, all fWeight 700) collapse to the same name. Where a label is shared by
// more than one font, rebuild it from the PostScript name, which uniquely
// encodes the variant; fall back to appending the raw font name if that still
// collides.
const disambiguateFontOptionLabels = (options: FontSelectOption[]): void => {
  const byLabel = new Map<string, FontSelectOption[]>();
  options.forEach((option) => {
    const group = byLabel.get(option.label) ?? [];
    group.push(option);
    byLabel.set(option.label, group);
  });

  byLabel.forEach((group) => {
    if (group.length <= 1) {
      return;
    }

    const rebuilt = group.map((option) =>
      [
        humanizeFontToken(option.family),
        getFontVariantFromPostScriptName(option.fontName, option.family),
      ]
        .filter(Boolean)
        .join(" ")
        .trim()
    );
    const allUnique =
      rebuilt.every(Boolean) &&
      new Set(rebuilt.map((label) => label.toLowerCase())).size ===
        group.length;

    group.forEach((option, index) => {
      const nextLabel = allUnique
        ? rebuilt[index]
        : `${option.label} (${option.fontName ?? option.value})`;
      option.label = nextLabel;
      // The dropdown renders the font's preview sample text (preview.text),
      // which the player also built from the colliding family+weight name — keep
      // it in sync so the picker shows the disambiguated name too.
      if (option.preview) {
        option.preview = { ...option.preview, text: nextLabel };
      }
    });
  });
};

const buildFontReplacement = (
  fontName: string,
  font?: MotionFontInfo["font"],
  metadata?: MotionFontInfo["metadata"]
): MotionReplaceFontConfig => ({
  fontName,
  ...(font ? { font } : {}),
  ...(metadata ? { metadata } : {}),
});

export const FONT_SOURCE_ORDER: Record<FontSelectOption["source"], number> = {
  animation: 0,
  account: 1,
  google: 2,
};

const compareFontSelectOptions = (
  left: FontSelectOption,
  right: FontSelectOption
) => {
  if (left.source === right.source) {
    return left.label.localeCompare(right.label);
  }

  return FONT_SOURCE_ORDER[left.source] - FONT_SOURCE_ORDER[right.source];
};

const getFontSelectOptionKey = (
  source: FontSelectOption["source"],
  fontName: string
) => `${source}:${fontName}`;

export type AccountFontVariant = {
  name: string;
  weight: number | string;
  style: string;
  url?: string | null;
};

export type AccountFont = {
  fontFamily: string;
  variants: AccountFontVariant[];
};

const isItalicFontStyle = (style?: string | null) =>
  Boolean(style && /italic|oblique/i.test(style));

const stripSpaces = (value: string) => value.replace(/\s+/g, "");

const accountFaceName = (family: string, styleName: string) => {
  const baseName = `${stripSpaces(family)}-${stripSpaces(styleName)}`;
  if (!/\s/.test(family) && !/\s/.test(styleName)) return baseName;

  // Qualify names containing whitespace even before another account font
  // collides with them. Otherwise adding a font would silently rename the
  // existing option and break its identity in the picker.
  const identity = `${family}\u0000${styleName}`;
  let hash = 2166136261;
  for (let i = 0; i < identity.length; i += 1) {
    hash = Math.imul(hash ^ identity.charCodeAt(i), 16777619);
  }
  return `${baseName}-${(hash >>> 0).toString(36)}`;
};

export type AccountFontFace = {
  fontName: string;
  family: string;
  styleName: string;
  style: "normal" | "italic";
  weight: string;
  url: string;
};

// The account stores a variant's subfamily ("Regular", "Bold Italic"), which
// repeats across families; qualify it the way a PostScript name does, so
// options, mods, loaded font faces and the player's fonts.list stay
// unambiguous. Variants without a url can't be loaded or applied, so they drop.
export const getAccountFontFaces = (
  accountFonts: AccountFont[]
): AccountFontFace[] =>
  accountFonts.flatMap((accountFont) =>
    accountFont.variants.flatMap((variant) => {
      if (!variant.url) {
        return [];
      }

      const styleName =
        humanizeFontToken(variant.name) ||
        humanizeFontToken(variant.style) ||
        "Regular";

      return [
        {
          fontName: accountFaceName(accountFont.fontFamily, styleName),
          family: accountFont.fontFamily,
          styleName,
          style: (isItalicFontStyle(variant.name) ||
          isItalicFontStyle(variant.style)
            ? "italic"
            : "normal") as "normal" | "italic",
          weight: String(variant.weight),
          url: variant.url,
        },
      ];
    })
  );

export const buildFontSelectOptions = (
  fontsInspection: MotionFontsInspection | undefined,
  layerFont: MotionLayerFontInfo | undefined,
  availableFonts: MotionGoogleFontInfo[] = [],
  accountFonts: AccountFont[] = []
): FontSelectOption[] => {
  const optionsByKey = new Map<string, FontSelectOption>();

  const addOption = (
    fontName: string | undefined,
    font?: MotionFontInfo["font"],
    metadata?: MotionFontInfo["metadata"],
    displayName?: string,
    preview?: MotionFontInfo["preview"]
  ) => {
    if (!fontName) {
      return;
    }

    const optionKey = getFontSelectOptionKey("animation", fontName);

    if (optionsByKey.has(optionKey)) {
      return;
    }

    optionsByKey.set(optionKey, {
      value: fontName,
      label: displayName || buildFontOptionLabel(fontName, font, metadata),
      replacement: buildFontReplacement(fontName, font, metadata),
      source: "animation",
      fontName,
      family: font?.fFamily || metadata?.family,
      styleName: font?.fStyle || metadata?.style,
      weight: font?.fWeight || metadata?.weight,
      preview,
    });
  };

  fontsInspection?.fonts.forEach((fontInfo) => {
    addOption(
      fontInfo.fontName,
      fontInfo.font,
      fontInfo.metadata,
      fontInfo.displayName,
      fontInfo.preview
    );
  });

  addOption(
    layerFont?.fontKey ?? layerFont?.fontName,
    layerFont?.font,
    layerFont?.metadata,
    layerFont?.displayName
  );

  getAccountFontFaces(accountFonts).forEach((face) => {
    const optionKey = getFontSelectOptionKey("account", face.fontName);

    if (optionsByKey.has(optionKey)) {
      return;
    }

    optionsByKey.set(optionKey, {
      value: face.fontName,
      label: `${face.family} ${face.styleName}`,
      // The player writes url + source into fonts.list / meta_b.fffDef, the
      // same shape it builds for a Google variant's stylesheet.
      replacement: {
        fontName: face.fontName,
        family: face.family,
        style: face.style,
        weight: face.weight,
        url: face.url,
        source: inferFontOverrideSource(face.url) ?? "font",
      },
      source: "account",
      fontName: face.fontName,
      family: face.family,
      style: face.style,
      styleName: face.styleName,
      weight: face.weight,
      // Rendered by the picker with the face loaded by useAccountFontFaces.
      familyPreview: { mode: "hostRender", text: face.family },
      preview: { mode: "hostRender", text: face.styleName },
    });
  });

  availableFonts.forEach((googleFont) => {
    googleFont.variants.forEach((variant) => {
      const optionKey = getFontSelectOptionKey("google", variant.fontName);

      if (optionsByKey.has(optionKey)) {
        return;
      }

      optionsByKey.set(optionKey, {
        value: variant.fontName,
        label: `${variant.family} ${variant.styleName}`,
        replacement: variant.replaceFontConfig,
        source: "google",
        fontName: variant.fontName,
        family: variant.family,
        style: variant.style,
        styleName: variant.styleName,
        weight: variant.weight,
        weightName: variant.weightName,
        stylesheetUrl: variant.stylesheetUrl,
        familyPreview: googleFont.preview,
        preview: variant.preview,
      });
    });
  });

  const built = Array.from(optionsByKey.values());
  disambiguateFontOptionLabels(built);

  return built.sort((left, right) => compareFontSelectOptions(left, right));
};

type KeyframedVectorValue = {
  t: number;
  s: number[];
  e?: number[];
  h?: number;
};

const isFiniteNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) &&
  value.every((entry) => typeof entry === "number" && Number.isFinite(entry));

const isKeyframedVectorValue = (
  value: unknown
): value is KeyframedVectorValue =>
  !!value &&
  typeof value === "object" &&
  "t" in value &&
  "s" in value &&
  typeof (value as { t?: unknown }).t === "number" &&
  Number.isFinite((value as { t?: number }).t) &&
  isFiniteNumberArray((value as { s?: unknown }).s);

const isKeyframedVectorValueList = (
  value: unknown
): value is KeyframedVectorValue[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(isKeyframedVectorValue);

const getKeyframedVectorValueAtFrame = (
  keyframes: KeyframedVectorValue[],
  frame: number
) => {
  const sortedKeyframes = [...keyframes].sort(
    (left, right) => left.t - right.t
  );
  const firstKeyframe = sortedKeyframes[0];

  if (frame <= firstKeyframe.t) {
    return [...firstKeyframe.s];
  }

  for (let index = 0; index < sortedKeyframes.length; index += 1) {
    const currentKeyframe = sortedKeyframes[index];
    const nextKeyframe = sortedKeyframes[index + 1];

    if (!nextKeyframe || frame <= nextKeyframe.t) {
      const endValue =
        currentKeyframe.e ?? nextKeyframe?.s ?? currentKeyframe.s;
      const duration = Math.max(
        1,
        (nextKeyframe?.t ?? currentKeyframe.t) - currentKeyframe.t
      );
      const progress =
        currentKeyframe.h === 1
          ? 0
          : Math.min(1, Math.max(0, (frame - currentKeyframe.t) / duration));

      return currentKeyframe.s.map((entry, entryIndex) => {
        const nextEntry = endValue[entryIndex] ?? entry;
        return entry + (nextEntry - entry) * progress;
      });
    }
  }

  return [...sortedKeyframes[sortedKeyframes.length - 1].s];
};

export const normalizeEditableFieldAnimationMetadata = (
  fields: VisualEditableField[],
  frame: number
) =>
  fields.map((field) => {
    if (
      field.isAnimated ||
      !field.canAnimate ||
      !isKeyframedVectorValueList(field.value)
    ) {
      return field;
    }

    return {
      ...field,
      value: getKeyframedVectorValueAtFrame(field.value, frame),
      isAnimated: true,
      keyframeCount: field.value.length,
    };
  });

export const buildFieldLookup = (fields: VisualEditableField[]) => {
  const lookup: FieldLookup = new Map();
  fields.forEach((field) => {
    const existingField = lookup.get(field.property);
    if (!existingField || (!existingField.controlled && field.controlled)) {
      lookup.set(field.property, field);
    }
  });
  return lookup;
};

const serializeComparableFieldValue = (value: unknown) => {
  if (typeof value === "undefined") {
    return "__undefined__";
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export const areEditableFieldValuesEqual = (left: unknown, right: unknown) =>
  serializeComparableFieldValue(left) === serializeComparableFieldValue(right);

export const isMixedValueField = (
  field: VisualEditableField | undefined
): boolean => Boolean(field?.isMixedValue);

const buildMultiField = (
  fields: VisualEditableField[]
): VisualEditableField => {
  const [primaryField] = fields;
  const controls = fields.flatMap((field) => field.controls ?? []);
  const controlled = fields.some((field) => field.controlled);
  const isMixedValue = fields.some(
    (field) => !areEditableFieldValuesEqual(field.value, primaryField.value)
  );

  return {
    ...primaryField,
    isMixedValue,
    targetFields: fields,
    canAnimate: fields.every((field) => field.canAnimate),
    isAnimated: fields.some((field) => field.isAnimated),
    keyframeCount: Math.max(...fields.map((field) => field.keyframeCount)),
    ...(controlled
      ? {
          controlled: true,
          controls,
          controlState:
            controls.length > 1 ? ("conflict" as const) : "controlled",
          writePolicy: fields.every(
            (field) => field.writePolicy === "edit-json"
          )
            ? ("edit-json" as const)
            : ("choose-target" as const),
        }
      : {}),
  };
};

export const buildCommonFieldLookup = (fieldLookups: FieldLookup[]) => {
  if (fieldLookups.length <= 1) {
    return fieldLookups[0] ?? new Map<EditableProperty, VisualEditableField>();
  }

  const lookup: FieldLookup = new Map();
  fieldLookups[0].forEach((field, property) => {
    const fields = fieldLookups
      .map((entry) => entry.get(property))
      .filter((entry): entry is VisualEditableField => Boolean(entry));

    if (fields.length === fieldLookups.length) {
      lookup.set(property, buildMultiField([field, ...fields.slice(1)]));
    }
  });

  return lookup;
};

export const getFirstField = (
  fields: FieldLookup,
  properties: EditableProperty[]
) => properties.map((property) => fields.get(property)).find(Boolean);

export const getAnimatedFields = (
  fields: Array<VisualEditableField | undefined>
) =>
  fields.filter((field): field is VisualEditableField =>
    Boolean(field?.isAnimated)
  );

type FieldControlMatchOptions = {
  componentIndex?: number;
};

const normalizeConnectionPath = (path: string | undefined) =>
  path?.replace(/\[(\d+)\]/g, ".$1").replace(/^\./, "");

const getFieldControlBasePaths = (field: VisualEditableField) =>
  [field.rootPath, field.path, getPendingFieldPath(field)]
    .map(normalizeConnectionPath)
    .filter((path): path is string => Boolean(path));

const getFieldControlPathCandidates = (
  basePath: string,
  componentIndex: number | undefined
) => {
  if (
    componentIndex !== undefined &&
    Number.isInteger(componentIndex) &&
    componentIndex >= 0
  ) {
    return [`${basePath}.${componentIndex}`, `${basePath}.k.${componentIndex}`];
  }

  return [basePath, `${basePath}.k`];
};

const getFieldControlPropertyCandidates = (
  field: VisualEditableField,
  componentIndex: number | undefined
) => {
  const candidates = [field.property];

  if (
    componentIndex !== undefined &&
    Number.isInteger(componentIndex) &&
    componentIndex >= 0
  ) {
    const componentSuffix = ["X", "Y", "Z"][componentIndex];
    if (componentSuffix) {
      candidates.push(`${field.property}${componentSuffix}`);
    }
  }

  return candidates;
};

const isFieldControlMatch = (
  field: VisualEditableField,
  control: FieldConnectionRef,
  options: FieldControlMatchOptions = {}
) => {
  const targetPath = normalizeConnectionPath(control.target.path);
  const basePaths = getFieldControlBasePaths(field);

  if (!targetPath) {
    const targetProperty = control.target.property;
    return Boolean(
      targetProperty &&
        getFieldControlPropertyCandidates(
          field,
          options.componentIndex
        ).includes(targetProperty)
    );
  }

  return basePaths.some((basePath) => {
    const candidates = getFieldControlPathCandidates(
      basePath,
      options.componentIndex
    );

    if (candidates.includes(targetPath)) {
      return true;
    }

    return (
      options.componentIndex === undefined &&
      targetPath.startsWith(`${basePath}.k.`)
    );
  });
};

const dedupeControls = (controls: FieldConnectionRef[]) => {
  const controlsById = new Map<string, FieldConnectionRef>();
  controls.forEach((control) => {
    controlsById.set(control.id, control);
  });
  return Array.from(controlsById.values());
};

export const getActiveFieldControls = (
  fields: Array<VisualEditableField | undefined>,
  options: FieldControlMatchOptions = {}
): FieldConnectionRef[] =>
  dedupeControls(
    fields.flatMap((field) =>
      (field?.controls ?? []).filter(
        (control) =>
          !control.disabled &&
          (!field || isFieldControlMatch(field, control, options))
      )
    )
  );

export const mergeConnectionControlsIntoFields = (
  fields: VisualEditableField[],
  controls: FieldConnectionRef[]
): VisualEditableField[] => {
  if (!controls.length) {
    return fields;
  }

  return fields.map((field) => {
    const matchingControls = controls.filter((control) =>
      isFieldControlMatch(field, control)
    );

    if (!matchingControls.some((control) => !control.disabled)) {
      return field;
    }

    const mergedControls = dedupeControls([
      ...(field.controls ?? []),
      ...matchingControls,
    ]);

    return {
      ...field,
      controlled: true,
      controls: mergedControls,
      controlState:
        mergedControls.filter((control) => !control.disabled).length > 1
          ? "conflict"
          : field.controlState ?? "controlled",
      writePolicy: field.writePolicy ?? "blocked",
    };
  });
};

export const isFieldEditingBlocked = (field: VisualEditableField | undefined) =>
  Boolean(field?.controlled);

export const hasBlockedField = (
  fields: Array<VisualEditableField | undefined>
) => fields.some(isFieldEditingBlocked);

export const hasAnyField = (fields: Array<VisualEditableField | undefined>) =>
  fields.some(Boolean);

export const updateVectorComponent = (
  field: VisualEditableField,
  index: number,
  nextEntry: number,
  onChange: (field: VisualEditableField, value: unknown) => void
) => {
  const values = toVector(field);
  const nextValue = [...values];
  nextValue[index] = nextEntry;
  onChange(field, nextValue);
};

export const updateLinkedScaleComponent = (
  field: VisualEditableField,
  index: number,
  nextEntry: number,
  onChange: (field: VisualEditableField, value: unknown) => void
) => {
  const values = toVector(field);
  const nextValue = [...values];
  const otherIndex = index === 0 ? 1 : 0;
  const currentSource = values[index];
  const currentOther = values[otherIndex];

  nextValue[index] = nextEntry;
  if (
    typeof currentSource === "number" &&
    Number.isFinite(currentSource) &&
    typeof currentOther === "number" &&
    Number.isFinite(currentOther)
  ) {
    nextValue[otherIndex] = roundToTwoDecimals(
      currentSource === 0
        ? nextEntry
        : (currentOther / currentSource) * nextEntry
    );
  } else {
    nextValue[otherIndex] = nextEntry;
  }

  onChange(field, nextValue);
};

export type ConnectionDisplayInfo = {
  label: string;
  className: string;
  detail?: string;
  title: string;
};

const formatPreviewValue = (value: unknown): string => {
  if (typeof value === "undefined") return "";
  if (value == null) return String(value);
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

export const getConnectionDisplayInfo = (
  control: FieldConnectionRef
): ConnectionDisplayInfo => {
  const valueSource = control.valueSource;
  const resolvedValue = formatPreviewValue(valueSource.evaluated);
  const rawValue = formatPreviewValue(valueSource.raw);
  const preview = resolvedValue || rawValue;

  const withPreview = (
    label: string,
    className: string,
    detail?: string
  ): ConnectionDisplayInfo => ({
    label,
    className,
    detail,
    title: [
      detail ? `${label}: ${detail}` : label,
      preview ? `Resolved value: ${preview}` : "",
      valueSource.status ? `Status: ${valueSource.status}` : "",
    ]
      .filter(Boolean)
      .join("\n"),
  });

  switch (control.writeBehavior) {
    case "countdown-runtime":
      return withPreview("Countdown", "countdown", control.target.timeUnit);
    case "create-input":
      return withPreview("Input field", "input-field", valueSource.key);
    case "interactive-action":
      return withPreview("Interactive", "input", valueSource.key);
  }

  switch (valueSource.kind) {
    case "dataKey":
      return withPreview("Data", "data", valueSource.key);
    case "liveControlKey":
      return withPreview("Control", "control", valueSource.key);
    case "expression":
    case "function":
      return withPreview("Code", "code", valueSource.key);
    case "inputName":
      return withPreview("Input", "input", valueSource.key);
    case "templateValue":
      return withPreview("Template", "code", preview);
    case "staticValue":
      return withPreview("Value", "input", preview);
    case "defaultValue":
      return withPreview("Default", "input", preview);
    case "runtime":
    default:
      return withPreview("Unset", "input");
  }
};
